import type {
  ContentPart,
  ProviderFinishReason,
  ProviderMessage,
  ProviderReasoningDetail,
  ProviderRequest,
  ProviderResponse,
  ProviderStreamChunk,
  ProviderTool,
  ProviderToolCallPart,
  ProviderUsage,
  ProviderUsagePricing,
} from '@standardagents/spec';

export type NovitaChatContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string; detail?: 'auto' | 'low' | 'high' } };

export type NovitaChatMessage =
  | { role: 'system'; content: string; name?: string }
  | { role: 'user'; content: string | NovitaChatContentPart[]; name?: string }
  | {
      role: 'assistant';
      content?: string | null;
      tool_calls?: NovitaChatToolCall[];
      reasoning_content?: string | null;
    }
  | { role: 'tool'; tool_call_id: string; content: string };

export interface NovitaChatToolCall {
  id: string;
  type: 'function';
  index?: number;
  function: {
    name: string;
    arguments: string;
  };
}

export interface NovitaChatTool {
  type: 'function';
  function: {
    name: string;
    description?: string;
    parameters?: Record<string, unknown>;
    strict?: boolean;
  };
}

export type NovitaChatToolChoice =
  | 'auto'
  | 'none'
  | 'required'
  | { type: 'function'; function: { name: string } };

export interface NovitaChatCompletionRequest {
  model: string;
  messages: NovitaChatMessage[];
  tools?: NovitaChatTool[];
  tool_choice?: NovitaChatToolChoice;
  parallel_tool_calls?: boolean;
  max_tokens?: number;
  temperature?: number;
  top_p?: number;
  stop?: string[];
  stream?: boolean;
  stream_options?: { include_usage?: boolean };
  user?: string;
  response_format?:
    | { type: 'json_object' }
    | { type: 'json_schema'; json_schema: { name: string; schema: Record<string, unknown>; strict?: boolean } };
  n?: number;
  seed?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  repetition_penalty?: number;
  top_k?: number;
  min_p?: number;
  logit_bias?: Record<string, number>;
  logprobs?: boolean;
  top_logprobs?: number;
  separate_reasoning?: boolean;
  enable_thinking?: boolean;
  modalities?: string[];
  [key: string]: unknown;
}

export interface NovitaChatCompletionUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  prompt_tokens_details?: {
    cached_tokens?: number;
    cache_creation_input_tokens?: number;
    cache_read_input_tokens?: number;
    text_tokens?: number;
    image_tokens?: number;
    video_tokens?: number;
    audio_tokens?: number;
  } | null;
  completion_tokens_details?: {
    reasoning_tokens?: number;
  } | null;
}

export interface NovitaChatCompletionResponse {
  id: string;
  object?: string;
  created?: number;
  model?: string;
  choices: Array<{
    index: number;
    message: {
      role: 'assistant';
      content: string | null;
      reasoning_content?: string | null;
      reasoning?: string | null;
      tool_calls?: NovitaChatToolCall[];
    };
    finish_reason: string;
  }>;
  usage?: NovitaChatCompletionUsage;
}

export interface NovitaChatStreamChunk {
  id?: string;
  object?: string;
  created?: number;
  model?: string;
  choices?: Array<{
    index: number;
    delta: {
      role?: string;
      content?: string | null;
      reasoning_content?: string | null;
      reasoning?: string | null;
      tool_calls?: Array<{
        index: number;
        id?: string;
        type?: 'function';
        function?: {
          name?: string;
          arguments?: string;
        };
      }>;
    };
    finish_reason?: string | null;
  }>;
  usage?: NovitaChatCompletionUsage | null;
}

function roundCost(value: number): number {
  return Number(value.toFixed(12));
}

export function calculateUsageCost(
  usage: ProviderUsage,
  pricing?: ProviderUsagePricing
): number | undefined {
  if (
    !pricing ||
    typeof pricing.inputPerMillion !== 'number' ||
    typeof pricing.outputPerMillion !== 'number'
  ) {
    return undefined;
  }

  const cachedTokens = usage.cachedTokens || 0;
  const uncachedPromptTokens = Math.max(0, usage.promptTokens - cachedTokens);
  const cachedRate = pricing.cachedInputPerMillion ?? pricing.inputPerMillion;

  return roundCost(
    (uncachedPromptTokens / 1_000_000) * pricing.inputPerMillion +
      (cachedTokens / 1_000_000) * cachedRate +
      (usage.completionTokens / 1_000_000) * pricing.outputPerMillion
  );
}

export function attachUsagePricing(
  usage: ProviderUsage,
  pricing?: ProviderUsagePricing
): ProviderUsage {
  if (!pricing) return usage;
  usage.pricing = pricing;
  const cost = calculateUsageCost(usage, pricing);
  if (cost !== undefined) usage.cost = cost;
  return usage;
}

export function transformChatContentPart(part: ContentPart): NovitaChatContentPart {
  if (part.type === 'text') {
    return { type: 'text', text: part.text };
  }

  if (part.type === 'image') {
    const data = part.data || '';
    const imageUrl = data.startsWith('data:')
      ? data
      : `data:${part.mediaType || 'image/png'};base64,${data}`;

    return {
      type: 'image_url',
      image_url: {
        url: imageUrl,
        detail: part.detail || 'auto',
      },
    };
  }

  if (part.type === 'image_url') {
    return {
      type: 'image_url',
      image_url: {
        url: part.image_url?.url || '',
        detail: part.image_url?.detail || 'auto',
      },
    };
  }

  return {
    type: 'text',
    text: `[File: ${part.filename || 'file'}]`,
  };
}

export function transformChatMessageContent(
  content: string | ContentPart[]
): string | NovitaChatContentPart[] {
  if (typeof content === 'string') {
    return content;
  }
  return content.map(transformChatContentPart);
}

export function transformChatMessages(messages: ProviderMessage[]): NovitaChatMessage[] {
  const result: NovitaChatMessage[] = [];

  for (const msg of messages) {
    switch (msg.role) {
      case 'system':
        result.push({
          role: 'system',
          content: msg.content,
        });
        break;
      case 'user':
        result.push({
          role: 'user',
          content: transformChatMessageContent(msg.content),
        });
        break;
      case 'assistant': {
        const message: NovitaChatMessage & { role: 'assistant' } = {
          role: 'assistant',
          content: msg.content || null,
        };

        if (msg.toolCalls && msg.toolCalls.length > 0) {
          message.tool_calls = msg.toolCalls.map((tc) => ({
            id: tc.id,
            type: 'function',
            function: {
              name: tc.name,
              arguments: typeof tc.arguments === 'string'
                ? tc.arguments
                : JSON.stringify(tc.arguments),
            },
          }));
        }

        if (msg.reasoning) {
          message.reasoning_content = msg.reasoning;
        }

        result.push(message);
        break;
      }
      case 'tool': {
        let output: string;

        if (typeof msg.content === 'string') {
          output = msg.content;
        } else if ('type' in msg.content) {
          if (msg.content.type === 'text') {
            output = msg.content.text;
          } else if (msg.content.type === 'error') {
            output = `Error: ${msg.content.error}`;
          } else {
            output = JSON.stringify(msg.content);
          }
        } else {
          output = JSON.stringify(msg.content);
        }

        const imageAttachments = msg.attachments?.filter((a) => a.type === 'image' && a.data) || [];
        result.push({
          role: 'tool',
          tool_call_id: msg.toolCallId,
          content: output || (imageAttachments.length > 0 ? 'Success' : ''),
        });

        if (imageAttachments.length > 0) {
          const imageContent: NovitaChatContentPart[] = [];
          const toolName = msg.toolName || 'the tool';
          const isSingle = imageAttachments.length === 1;
          const descriptor = isSingle ? 'the file' : `${imageAttachments.length} files`;
          const verb = isSingle ? 'is' : 'are';

          imageContent.push({
            type: 'text',
            text: `Here ${verb} ${descriptor} from ${toolName}:`,
          });

          for (const attachment of imageAttachments) {
            const attachmentData = attachment.data || '';
            const imageData = attachmentData.startsWith('data:')
              ? attachmentData
              : `data:${attachment.mediaType || 'image/png'};base64,${attachmentData}`;

            imageContent.push({
              type: 'image_url',
              image_url: {
                url: imageData,
                detail: 'auto',
              },
            });
          }

          result.push({
            role: 'user',
            content: imageContent,
          });
        }
        break;
      }
    }
  }

  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

const STRICT_UNSUPPORTED_KEYWORDS = new Set<string>([
  'minLength', 'maxLength', 'pattern', 'format',
  'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf',
  'minItems', 'maxItems', 'uniqueItems', 'minContains', 'maxContains',
  'minProperties', 'maxProperties', 'patternProperties', 'propertyNames',
  'dependentRequired', 'dependentSchemas', 'unevaluatedProperties',
  'default', 'examples', '$schema', '$comment', 'contentEncoding', 'contentMediaType',
]);

function normalizeStrictJsonSchema(schema: unknown): unknown {
  if (Array.isArray(schema)) {
    return schema.map((item) => normalizeStrictJsonSchema(item));
  }
  if (!isRecord(schema)) {
    return schema;
  }

  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(schema)) {
    if (STRICT_UNSUPPORTED_KEYWORDS.has(key)) continue;

    if (key === 'properties' && isRecord(value)) {
      const props: Record<string, unknown> = {};
      for (const [propName, propSchema] of Object.entries(value)) {
        props[propName] = normalizeStrictJsonSchema(propSchema);
      }
      normalized[key] = props;
    } else if (key === 'enum' || key === 'const') {
      normalized[key] = value;
    } else {
      normalized[key] = normalizeStrictJsonSchema(value);
    }
  }

  const properties = isRecord(normalized.properties) ? normalized.properties : undefined;
  const type = normalized.type;
  const isObjectSchema =
    type === 'object' || (Array.isArray(type) && type.includes('object')) || !!properties;

  if (isObjectSchema) {
    if (normalized.type === undefined) {
      normalized.type = 'object';
    }
    normalized.properties = properties || {};
    normalized.required = Object.keys(normalized.properties as Record<string, unknown>);
    normalized.additionalProperties = false;
  }

  return normalized;
}

function normalizeStrictToolParameters(
  parameters?: Record<string, unknown> | null
): Record<string, unknown> {
  const base = isRecord(parameters) ? { ...parameters } : {};
  if (base.type === undefined) base.type = 'object';
  if (base.properties === undefined) base.properties = {};
  return normalizeStrictJsonSchema(base) as Record<string, unknown>;
}

export function transformChatTool(tool: ProviderTool): NovitaChatTool {
  const inputParams = tool.function.parameters as Record<string, unknown> | null | undefined;

  return {
    type: 'function',
    function: {
      name: tool.function.name,
      description: tool.function.description || undefined,
      parameters: normalizeStrictToolParameters(inputParams),
      // No `strict` (2026-08-04): grammar-constrained decoding measurably
      // degraded Hy3's generation quality — three exact-replica runs without
      // it produced the best output of a 30-run campaign, and no trained
      // harness (Claude Code/opencode) sends it.
    },
  };
}

export function transformChatToolChoice(
  choice: ProviderRequest['toolChoice']
): NovitaChatToolChoice | undefined {
  if (choice === 'auto') return 'auto';
  if (choice === 'none') return 'none';
  if (choice === 'required') return 'required';
  if (typeof choice === 'object' && 'name' in choice) {
    return { type: 'function', function: { name: choice.name } };
  }
  return undefined;
}

export function mapChatFinishReason(finishReason: string | null | undefined): ProviderFinishReason {
  switch (finishReason) {
    case 'stop':
      return 'stop';
    case 'tool_calls':
    case 'function_call':
      return 'tool_calls';
    case 'length':
      return 'length';
    case 'content_filter':
      return 'content_filter';
    case 'error':
      return 'error';
    default:
      return 'stop';
  }
}

export function extractChatToolCalls(
  toolCalls: NovitaChatToolCall[] | undefined
): ProviderToolCallPart[] | undefined {
  if (!toolCalls || toolCalls.length === 0) {
    return undefined;
  }

  return toolCalls.map((tc) => {
    let parsedArgs: Record<string, unknown> = {};
    try {
      parsedArgs = tc.function.arguments ? JSON.parse(tc.function.arguments) : {};
    } catch {
      parsedArgs = {};
    }

    return {
      id: tc.id,
      name: tc.function.name,
      arguments: parsedArgs,
    };
  });
}

export function transformChatUsage(
  usage: NovitaChatCompletionUsage | null | undefined,
  pricing?: ProviderUsagePricing
): ProviderUsage {
  const mapped: ProviderUsage = {
    promptTokens: usage?.prompt_tokens || 0,
    completionTokens: usage?.completion_tokens || 0,
    totalTokens: usage?.total_tokens || 0,
    cachedTokens: usage?.prompt_tokens_details?.cached_tokens,
    reasoningTokens: usage?.completion_tokens_details?.reasoning_tokens,
  };

  return attachUsagePricing(mapped, pricing);
}

export function transformChatResponse(
  response: NovitaChatCompletionResponse,
  pricing?: ProviderUsagePricing
): ProviderResponse {
  const choice = response.choices?.[0];
  const message = choice?.message;
  const reasoning = message?.reasoning_content || message?.reasoning || null;

  return {
    content: message?.content || null,
    reasoning,
    reasoningDetails: reasoning ? [{ type: 'text', text: reasoning }] : undefined,
    toolCalls: extractChatToolCalls(message?.tool_calls),
    finishReason: mapChatFinishReason(choice?.finish_reason),
    usage: transformChatUsage(response.usage, pricing),
    metadata: {
      model: response.model,
      provider: 'novita',
      requestId: response.id,
      created: response.created,
      object: response.object,
    },
  };
}

export interface NovitaChatStreamState {
  model?: string;
  content: string;
  toolCalls: Map<number, { id: string; name: string; arguments: string }>;
  reasoningContent: string;
  hasContent: boolean;
  hasReasoning: boolean;
  finishReason: string | null;
}

export function createChatStreamState(model?: string): NovitaChatStreamState {
  return {
    model,
    content: '',
    toolCalls: new Map(),
    reasoningContent: '',
    hasContent: false,
    hasReasoning: false,
    finishReason: null,
  };
}

export function processChatStreamChunk(
  chunk: NovitaChatStreamChunk,
  state: NovitaChatStreamState
): ProviderStreamChunk[] {
  const chunks: ProviderStreamChunk[] = [];
  const choice = chunk.choices?.[0];
  const delta = choice?.delta;

  if (chunk.model) {
    state.model = chunk.model;
  }

  if (!delta && !choice?.finish_reason && !chunk.usage) {
    return chunks;
  }

  if (delta?.content) {
    state.hasContent = true;
    state.content += delta.content;
    chunks.push({ type: 'content-delta', delta: delta.content });
  }

  const reasoningDelta = delta?.reasoning_content || delta?.reasoning;
  if (reasoningDelta) {
    state.hasReasoning = true;
    state.reasoningContent += reasoningDelta;
    chunks.push({ type: 'reasoning-delta', delta: reasoningDelta });
  }

  if (delta?.tool_calls) {
    for (const tc of delta.tool_calls) {
      const existing = state.toolCalls.get(tc.index);

      if (tc.id && tc.function?.name) {
        state.toolCalls.set(tc.index, {
          id: tc.id,
          name: tc.function.name,
          arguments: tc.function.arguments || '',
        });
        chunks.push({
          type: 'tool-call-start',
          id: tc.id,
          name: tc.function.name,
        });
      } else if (existing && tc.function?.arguments) {
        existing.arguments += tc.function.arguments;
        chunks.push({
          type: 'tool-call-delta',
          id: existing.id,
          argumentsDelta: tc.function.arguments,
        });
      }
    }
  }

  if (choice?.finish_reason) {
    state.finishReason = choice.finish_reason;

    if (state.hasContent) {
      chunks.push({ type: 'content-done' });
    }

    if (state.hasReasoning) {
      chunks.push({ type: 'reasoning-done' });
    }

    for (const tc of state.toolCalls.values()) {
      let parsedArgs: Record<string, unknown> = {};
      try {
        parsedArgs = tc.arguments ? JSON.parse(tc.arguments) : {};
      } catch {
        parsedArgs = {};
      }

      chunks.push({
        type: 'tool-call-done',
        id: tc.id,
        arguments: parsedArgs,
      });
    }
  }

  if (chunk.usage) {
    const reasoningDetails: ProviderReasoningDetail[] | undefined = state.reasoningContent
      ? [{ type: 'text', text: state.reasoningContent }]
      : undefined;

    chunks.push({
      type: 'finish',
      finishReason: mapChatFinishReason(state.finishReason),
      usage: transformChatUsage(chunk.usage),
      reasoningDetails,
    });
  }

  return chunks;
}

export function parseChatStreamEvent(jsonStr: string): NovitaChatStreamChunk | null {
  try {
    return JSON.parse(jsonStr) as NovitaChatStreamChunk;
  } catch {
    return null;
  }
}

export async function* parseChatSSEStream(
  response: Response,
  state: NovitaChatStreamState
): AsyncIterable<ProviderStreamChunk> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue;
        if (!trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') continue;

        const chunk = parseChatStreamEvent(data);
        if (!chunk) continue;

        for (const providerChunk of processChatStreamChunk(chunk, state)) {
          yield providerChunk;
        }
      }
    }

    if (buffer.trim()) {
      const trimmed = buffer.trim();
      if (trimmed.startsWith('data: ')) {
        const data = trimmed.slice(6);
        if (data !== '[DONE]') {
          const chunk = parseChatStreamEvent(data);
          if (chunk) {
            for (const providerChunk of processChatStreamChunk(chunk, state)) {
              yield providerChunk;
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export function buildChatParams(request: ProviderRequest): NovitaChatCompletionRequest {
  const params: NovitaChatCompletionRequest = {
    model: request.model,
    messages: transformChatMessages(request.messages),
  };

  if (request.tools && request.tools.length > 0) {
    params.tools = request.tools.map(transformChatTool);

    const toolChoice = transformChatToolChoice(request.toolChoice);
    if (toolChoice !== undefined) {
      params.tool_choice = toolChoice;
    }

    if (request.parallelToolCalls !== undefined) {
      params.parallel_tool_calls = request.parallelToolCalls;
    }
  }

  if (request.maxOutputTokens !== undefined) {
    params.max_tokens = request.maxOutputTokens;
  }

  if (request.temperature !== undefined) {
    params.temperature = request.temperature;
  }

  if (request.topP !== undefined) {
    params.top_p = request.topP;
  }

  // Stable per-thread identity → replica-sticky routing. Novita load-balances
  // models across replicas with per-replica prefix caches; without `user`,
  // consecutive requests land on random replicas and only the
  // universally-warm system prompt ever hits (measured 3-4% cached on Hy3
  // while a byte-identical replay to a warm replica cached 99.9%).
  if (request.user) {
    params.user = request.user;
  }

  if (request.stopSequences && request.stopSequences.length > 0) {
    params.stop = request.stopSequences;
  }

  // No explicit enable_thinking/separate_reasoning (2026-08-04): Novita's
  // serving defaults already run Hy3 thinking in tool mode, and the exact
  // -replica campaign showed the explicit stamps are at best neutral. Callers
  // can still force them via providerOptions.

  if (request.responseFormat?.type === 'json') {
    if (request.responseFormat.schema) {
      params.response_format = {
        type: 'json_schema',
        json_schema: {
          name: 'response',
          schema: normalizeStrictJsonSchema(request.responseFormat.schema) as Record<string, unknown>,
          strict: true,
        },
      };
    } else {
      params.response_format = { type: 'json_object' };
    }
  }

  if (request.providerOptions) {
    const { _metadata, ...safeOptions } = request.providerOptions as Record<string, unknown> & {
      _metadata?: unknown;
    };
    Object.assign(params, safeOptions);
  }

  return params;
}

export function createChatErrorChunk(error: string, code?: string): ProviderStreamChunk {
  return { type: 'error', error, code };
}

function isBase64Like(str: string): boolean {
  if (str.startsWith('data:')) return true;
  if (str.length > 200) {
    const base64Pattern = /^[A-Za-z0-9+/]+=*$/;
    return base64Pattern.test(str.substring(0, 200));
  }
  return false;
}

function truncateBase64String(str: string, maxLength: number = 50): string {
  if (str.length <= maxLength) return str;
  const preview = str.substring(0, maxLength);
  return `${preview}...[truncated, ${str.length.toLocaleString()} chars]`;
}

export function truncateChatBase64<T>(obj: T, maxLength: number = 50): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return (isBase64Like(obj) ? truncateBase64String(obj, maxLength) : obj) as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => truncateChatBase64(item, maxLength)) as T;
  }

  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = truncateChatBase64(value, maxLength);
    }
    return result as T;
  }

  return obj;
}
